import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, MessageCircle, Settings, Brain, ArrowRight, CheckCircle, Zap, Database, Filter, TrendingUp, Globe, Code } from 'lucide-react';

const RFPAssistantProject = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const coreFeatures = [
    { icon: Brain, title: 'FAQ + RAG Dual Path', desc: 'Combines FAQ similarity search with RAG for comprehensive context retrieval' },
    { icon: Filter, title: 'Configurable Thresholds', desc: 'Flexible threshold control to filter and use only high-quality context' },
    { icon: MessageCircle, title: 'User Feedback Loop', desc: 'V2 introduces feedback input for more accurate answers on retry' },
    { icon: Code, title: 'Auto FAQ Generation', desc: 'Retry questions automatically formatted for FAQ API import' }
  ];

  const v1Features = [
    { icon: Upload, title: 'Excel File Upload', desc: 'Drag & drop Excel files with questions' },
    { icon: Settings, title: 'API Configuration', desc: 'OpenAI, Similarity APIs setup' },
    { icon: Zap, title: 'Batch Processing', desc: 'Process hundreds of questions at once' },
    { icon: Database, title: 'Direct Excel Export', desc: 'Save answers back to Excel file' },
    { icon: MessageCircle, title: 'Individual Retry', desc: 'Retry specific questions as needed' },
    { icon: Globe, title: 'Desktop App', desc: 'Standalone executable application' }
  ];

  const v2Features = [
    { icon: Globe, title: 'Web Platform', desc: 'Multi-user access, login to use' },
    { icon: Upload, title: 'Enhanced Upload', desc: 'Drag & drop with validation' },
    { icon: Settings, title: 'Advanced Config', desc: 'LocalStorage-based configuration' },
    { icon: Filter, title: 'Threshold Control', desc: 'Configurable FAQ/RAG thresholds' },
    { icon: MessageCircle, title: 'Feedback-Driven Retry', desc: 'User feedback for precise answers' },
    { icon: Code, title: 'FAQ Auto-Import', desc: 'Export feedback to FAQ via API' }
  ];

  const workflow = [
    {
      step: '01',
      title: 'Upload Excel File',
      icon: Upload,
      desc: 'Upload Excel containing presales or technical questions',
      details: ['Drag & drop interface', 'Automatic question detection', 'Category hierarchy parsing', 'Database storage']
    },
    {
      step: '02',
      title: 'Dual-Path Context Retrieval',
      icon: Brain,
      desc: 'FAQ and RAG APIs fetch relevant information in parallel',
      details: ['FAQ similarity search', 'RAG document retrieval', 'Threshold-based filtering', 'High-quality context selection']
    },
    {
      step: '03',
      title: 'AI-Powered Answer Generation',
      icon: Zap,
      desc: 'OpenAI generates answers using filtered context',
      details: ['Context-aware responses', 'Real-time processing', 'WebSocket progress updates', 'Answer validation']
    },
    {
      step: '04',
      title: 'Review & Optimize',
      icon: TrendingUp,
      desc: 'Review answers, retry with feedback, export to Excel or FAQ',
      details: ['User feedback input', 'Precise retry mechanism', 'Excel export', 'FAQ API import']
    }
  ];

  const v2Workflow = [
    {
      step: '01',
      title: 'Upload & Parse',
      icon: Upload,
      desc: 'Upload Excel with configurable parsing rules'
    },
    {
      step: '02',
      title: 'Batch Process',
      icon: Zap,
      desc: 'Async processing with WebSocket progress'
    },
    {
      step: '03',
      title: 'Review & Feedback',
      icon: MessageCircle,
      desc: 'View answers, provide feedback for retry'
    },
    {
      step: '04',
      title: 'Export Results',
      icon: Database,
      desc: 'Export to Excel or import to FAQ via API'
    }
  ];

  const businessImpact = [
    {
      value: '85%',
      label: 'Time Saved',
      desc: 'Automated question answering vs manual research',
      color: 'from-orange-500 to-red-500'
    },
    {
      value: '2x',
      label: 'Accuracy Improvement',
      desc: 'V2 feedback-driven precision enhancement',
      color: 'from-pink-500 to-rose-500'
    },
    {
      value: '100+',
      label: 'Questions/Hour',
      desc: 'Batch processing capability per user',
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/30 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 rounded-lg">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-orange-400">RFP Assistant</h3>
          <p className="text-sm text-gray-400">AI-Powered Question Answering for Presales & Technical Queries</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mb-6 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
        <p className="text-gray-300 leading-relaxed">
          Batch answering tool for <span className="text-orange-400 font-bold">presales questions</span> and
          <span className="text-red-400 font-bold"> technical queries</span>, combining
          <span className="text-yellow-400 font-bold"> FAQ + RAG dual-path retrieval</span> with
          <span className="text-pink-400 font-bold"> configurable thresholds</span> for high-quality AI-generated responses.
        </p>
      </div>

      {/* Core Highlights */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/30 mb-6">
        <h4 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Key Highlights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coreFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-2 rounded-lg flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-white mb-1">{feature.title}</h5>
                  <p className="text-xs text-gray-400">{feature.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Version Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* V1 - App Version */}
        <div className="bg-white/5 rounded-xl border border-orange-500/30 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500/20 to-orange-500/10 p-4 border-b border-orange-500/20">
            <div className="flex items-center gap-3">
              <div className="bg-orange-500 p-2 rounded-lg">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">V1 - Desktop App</h4>
                <p className="text-xs text-gray-400">Standalone Executable</p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-3 mb-4">
              {v1Features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-2">
                    <Icon className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-semibold text-white">{feature.title}</h5>
                      <p className="text-xs text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* V1 Screenshots - Stacked with reveal effect */}
            <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden mt-4">
              {/* Background image - config */}
              <div className="absolute inset-0">
                <img
                  src="/pic/rfp/app_config.webp"
                  alt="App v1 Config"
                  className="w-full h-full object-cover object-left-top"
                />
              </div>

              {/* Foreground image - main app */}
              <div
                className="absolute inset-0 transition-all duration-500 ease-in-out cursor-pointer group"
                style={{ right: '25%' }}
                onClick={() => setSelectedImage({ src: '/pic/rfp/app.webp', title: 'RFP Assistant Desktop App v1 - Main Interface' })}
              >
                <img
                  src="/pic/rfp/app.webp"
                  alt="App v1 Main"
                  className="w-full h-full object-cover object-left-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-0 right-0 text-center">
                    <p className="text-white text-xs font-semibold">Main Interface</p>
                  </div>
                </div>
              </div>

              {/* Revealed config image on hover */}
              <div
                className="absolute inset-0 group"
                onMouseEnter={(e) => {
                  e.currentTarget.previousElementSibling.style.right = '85%';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.previousElementSibling.style.right = '25%';
                }}
              >
                <div
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => setSelectedImage({ src: '/pic/rfp/app_config.webp', title: 'RFP Assistant Desktop App v1 - Configuration' })}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-2 right-2 text-center">
                      <p className="text-white text-xs font-semibold">Configuration →</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* V2 - Web Version */}
        <div className="bg-white/5 rounded-xl border border-red-500/30 overflow-hidden">
          <div className="bg-gradient-to-r from-red-500/20 to-red-500/10 p-4 border-b border-red-500/20">
            <div className="flex items-center gap-3">
              <div className="bg-red-500 p-2 rounded-lg">
                <Code className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">V2 - Web Platform</h4>
                <p className="text-xs text-gray-400">Multi-User Access (In Development)</p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-3 mb-4">
              {v2Features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-2">
                    <Icon className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-semibold text-white">{feature.title}</h5>
                      <p className="text-xs text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* V2 Screenshots - Stacked with reveal effect */}
            <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden mt-4">
              {/* Background image - config */}
              <div className="absolute inset-0">
                <img
                  src="/pic/rfp/web_config.webp"
                  alt="Web v2 Config"
                  className="w-full h-full object-cover object-left-top"
                />
              </div>

              {/* Foreground image - main web */}
              <div
                className="absolute inset-0 transition-all duration-500 ease-in-out cursor-pointer group"
                style={{ right: '25%' }}
                onClick={() => setSelectedImage({ src: '/pic/rfp/web.webp', title: 'RFP Assistant Web v2 - Main Interface' })}
              >
                <img
                  src="/pic/rfp/web.webp"
                  alt="Web v2 Main"
                  className="w-full h-full object-cover object-left-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-0 right-0 text-center">
                    <p className="text-white text-xs font-semibold">Main Interface</p>
                  </div>
                </div>
              </div>

              {/* Revealed config image on hover */}
              <div
                className="absolute inset-0 group"
                onMouseEnter={(e) => {
                  e.currentTarget.previousElementSibling.style.right = '85%';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.previousElementSibling.style.right = '25%';
                }}
              >
                <div
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => setSelectedImage({ src: '/pic/rfp/web_config.webp', title: 'RFP Assistant Web v2 - Configuration' })}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-2 right-2 text-center">
                      <p className="text-white text-xs font-semibold">Configuration →</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* V2 Workflow */}
      <div className="bg-white/5 rounded-xl p-6 border border-red-500/30 mb-6">
        <h4 className="text-lg font-bold text-red-400 mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5" />
          V2 Enhanced Workflow
        </h4>

        <div className="grid grid-cols-4 gap-3">
          {v2Workflow.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.step} className="text-center">
                <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-lg p-4 mb-2">
                  <Icon className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full inline-block mb-2">
                    {step.step}
                  </div>
                  <h5 className="text-xs font-bold text-white mb-1">{step.title}</h5>
                  <p className="text-xs text-gray-400">{step.desc}</p>
                </div>
                {index < v2Workflow.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-gray-400 mx-auto mt-2" />
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
          <h5 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            V2 Key Improvements
          </h5>
          <ul className="space-y-2">
            <li className="text-xs text-gray-300 flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">User Feedback Input:</strong> Must provide feedback when retrying for more accurate answers</span>
            </li>
            <li className="text-xs text-gray-300 flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Auto FAQ Import:</strong> Retry questions formatted for FAQ API to optimize future responses</span>
            </li>
            <li className="text-xs text-gray-300 flex items-start gap-2">
              <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
              <span><strong className="text-white">Configurable Thresholds:</strong> Flexible control over FAQ/RAG quality filtering</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Detailed Workflow (V1) */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/30 mb-6">
        <h4 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Complete Workflow
        </h4>

        <div className="space-y-4">
          {workflow.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.step} className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                      {step.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-white/5 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="bg-orange-500/20 p-2 rounded-lg">
                        <Icon className="w-5 h-5 text-orange-400" />
                      </div>
                      <div>
                        <h5 className="font-bold text-white mb-1">{step.title}</h5>
                        <p className="text-xs text-gray-400">{step.desc}</p>
                      </div>
                    </div>
                    <ul className="ml-10 space-y-1">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Arrow */}
                {index < workflow.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Business Impact */}
      <div className="bg-white/5 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-lg font-bold text-red-400 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Business Impact
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {businessImpact.map((impact, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gradient-to-br ${impact.color} rounded-lg p-4 relative overflow-hidden`}
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative">
                <div className="text-3xl font-bold text-white mb-1">{impact.value}</div>
                <div className="text-sm font-semibold text-white/90 mb-1">{impact.label}</div>
                <p className="text-xs text-white/80">{impact.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-6xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white hover:text-orange-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4">{selectedImage.title}</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default RFPAssistantProject;
